import React from 'react';
import { DetailedCoin } from '../types';
import './CoinBadges.css';

interface CoinBadgesProps {
  coin?: DetailedCoin;
  isNewFind?: boolean;
  timesFoundSoFar?: number;
  inline?: boolean;
  revealed?: boolean;
  showRarityBadge?: boolean;
  isExiting?: boolean;
  commemorativeLabel?: string;
}

export const CoinBadges: React.FC<CoinBadgesProps> = ({
  coin,
  isNewFind,
  timesFoundSoFar,
  inline = false,
  revealed = true,
  showRarityBadge = true,
  isExiting = false,
  commemorativeLabel = 'Commemorative'
}) => {
  if (!coin) {
    return <div className={inline ? 'badges-inline' : 'badges-container'} />;
  }

  // In full hunting inspection view, hide badges until the coin obverse is revealed
  if (!inline && !revealed && !isExiting) {
    return <div className="badges-container" />;
  }

  // Type badge
  const isCommemorative = coin.type === 'commemorative';

  // Strict mintage-based badge (Likely NIFC, NIFC, U100k, U500k, U1M, U5M)
  let mintageBadge: React.ReactNode = null;
  if (inline || showRarityBadge) {
    const popClass = !inline ? 'badge-pop-in' : '';
    const status = coin.status || coin.mintageFormatted;
    const isLikelyNifc = status === 'Likely NIFC';
    const isNifc = status === 'NIFC';
    const isCirc = status === 'CIRC';

    if (isLikelyNifc) {
      mintageBadge = <span className={`badge badge-nifc ${popClass}`.trim()}>Likely NIFC</span>;
    } else if (isNifc || coin.mintage === 0) {
      mintageBadge = <span className={`badge badge-nifc ${popClass}`.trim()}>NIFC</span>;
    } else if (!isCirc) {
      if (coin.mintage <= 100_000) {
        mintageBadge = <span className={`badge badge-u100k ${popClass}`.trim()}>U100k</span>;
      } else if (coin.mintage <= 500_000) {
        mintageBadge = <span className={`badge badge-u500k ${popClass}`.trim()}>U500k</span>;
      } else if (coin.mintage <= 1_000_000) {
        mintageBadge = <span className={`badge badge-u1m ${popClass}`.trim()}>U1M</span>;
      } else if (coin.mintage <= 5_000_000) {
        mintageBadge = <span className={`badge badge-u5m ${popClass}`.trim()}>U5M</span>;
      }
    }
  }

  const fadeClass = !inline ? 'badge-fade-in' : '';
  const containerClass = inline
    ? 'badges-inline'
    : `badges-container ${isExiting ? 'badges-exiting' : ''}`.trim();

  return (
    <div className={containerClass}>
      {/* 1. Issue Type Badge */}
      <span
        className={`badge ${
          isCommemorative ? 'badge-commemorative' : 'badge-standard'
        } ${fadeClass}`.trim()}
      >
        {isCommemorative ? commemorativeLabel : 'Standard'}
      </span>

      {/* 2. Collection Discovery Badge */}
      {isNewFind ? (
        <span className={`badge badge-new-find ${fadeClass}`.trim()}>New Find</span>
      ) : timesFoundSoFar && timesFoundSoFar > 1 ? (
        <span className={`badge badge-duplicate ${fadeClass}`.trim()}>Found {timesFoundSoFar}×</span>
      ) : null}

      {/* 3. Mintage Threshold Badge (appears when mintage finishes counting up) */}
      {mintageBadge}
    </div>
  );
};
